import { AgGridReact } from 'ag-grid-react';
import React, { useEffect } from 'react';

const ModernAgGrid = ({
  rowData,
  columnDefs,
  gridRef,
  headerHeight = 50,
  rowHeight = 50,
  animateRows = true,
  rowSelection = 'multiple',
  pagination = false,
  rowStyle = {
    borderBottom: '1px solid #f0f0f0',
    fontSize: '14px',
  },
  ...props
}) => {
  // Add custom styles for the modern table look
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .ag-header-modern {
        font-weight: 600;
        background: linear-gradient(to bottom, #f5f7fa 0%, #e4e7eb 100%);
        color: #2c3e50;
        border-bottom: 2px solid #d1d8e0;
        border-radius: 8px 8px 0 0;
      }
      .ag-row-modern {
        transition: background-color 0.2s ease;
      }
      .ag-row-hover {
        background-color: #f8f9fa !important;
      }
      .ag-row-selected {
        background-color: #e3f2fd !important;
      }
      .ag-cell {
        display: flex;
        align-items: center;
      }
      .ag-header-cell-text {
        font-size: 14px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      .ag-theme-alpine {
        --ag-font-size: 14px;
        --ag-header-height: 50px;
        --ag-row-height: 50px;
        --ag-border-color: #e0e0e0;
        --ag-header-foreground-color: #2c3e50;
        --ag-header-background-color: #f8f9fa;
        --ag-odd-row-background-color: #ffffff;
        --ag-even-row-background-color: #fafafa;
      }
      .ag-cell-wrapper {
        height: 100%;
        overflow: hidden;
      }
      .ag-cell-value {
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
      }
      .ag-theme-alpine .ag-center-cols-container,
      .ag-theme-alpine .ag-pinned-center-cols-container {
        max-height: none !important;
      }
    `;
    document.head.appendChild(style);

    // Cleanup function
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Process column definitions to add ellipsis and tooltip functionality
  const processedColumnDefs = columnDefs.map((col) => ({
    ...col,
    cellStyle: {
      ...(col.cellStyle || {}),
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      padding: '8px 4px',
    },
    tooltipField: col.field, // Show full text in tooltip
    // If a custom cellRenderer is provided, wrap it to maintain functionality
    cellRenderer: col.cellRenderer
      ? (params) => {
          const renderedContent = col.cellRenderer(params);
          // If it's a React element, wrap it with a div that has ellipsis
          if (React.isValidElement(renderedContent)) {
            return (
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {renderedContent}
              </div>
            );
          }
          // If it's a string or number, wrap it in a div with ellipsis
          return (
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
              }}
            >
              {renderedContent}
            </div>
          );
        }
      : undefined,
  }));

  return (
    <div
      className="ag-theme-alpine"
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        ...props.style,
      }}
    >
      <AgGridReact
        ref={gridRef}
        rowData={rowData}
        columnDefs={processedColumnDefs}
        rowClass="ag-row-modern"
        headerHeight={headerHeight}
        rowHeight={rowHeight}
        suppressCellFocus={false}
        domLayout="normal"
        animateRows={animateRows}
        rowSelection={rowSelection}
        pagination={pagination}
        rowStyle={rowStyle}
        headerClass="ag-header-modern"
        rowHoverHighlight
        tooltipShowDelay={0}
        enableCellTextSelection
        ensureDomOrder
        fillRemainingSpaceOnViewportResize
        suppressColumnVirtualisation={false}
        {...props}
      />
    </div>
  );
};

export default ModernAgGrid;
